import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, type AllowlistEntry, type Me } from "../api";
import { BackIcon } from "../components/icons";

/**
 * Minimal beta-allowlist manager for ADMIN_EMAILS accounts. The server
 * enforces authorization on every /api/admin call — `me.isAdmin` only
 * decides whether this page renders at all. Reachable from /account.
 */
export default function Admin({ me }: { me: Me }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [entries, setEntries] = useState<AllowlistEntry[] | null>(null);
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = async () => {
    try {
      setEntries(await api.listAllowlist());
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setEntries([]);
    }
  };

  useEffect(() => {
    if (me.isAdmin) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!me.isAdmin) return <Navigate to="/app" replace />;

  const trimmedEmail = email.trim();
  const canAdd = !busy && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmedEmail);

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canAdd) return;
    setBusy(true);
    setErr(null);
    try {
      await api.addAllowlistEmail(trimmedEmail, note.trim() || undefined);
      setEmail("");
      setNote("");
      await load();
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : String(e2));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (entry: AllowlistEntry) => {
    setErr(null);
    try {
      await api.removeAllowlistEmail(entry.email);
      await load();
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : String(e2));
    }
  };

  return (
    <div className="tp-app">
      <div className="tp-topbar">
        <button className="tp-topbtn" onClick={() => navigate("/app/account")} aria-label={t("common.back")}>
          <BackIcon />
        </button>
        <div className="tp-eyebrow">{t("admin.eyebrow")}</div>
        <div style={{ width: 38 }} />
      </div>

      <div className="tp-screen" style={{ paddingTop: 24 }}>
        <div className="tp-display" style={{ marginBottom: 8 }}>{t("admin.title")}</div>
        <div className="tp-body" style={{ color: "var(--tp-ink-soft)", marginBottom: 20, fontSize: 15 }}>
          {t("admin.intro")}
        </div>

        <form onSubmit={add} style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 24 }}>
          <input
            className="tp-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("admin.emailPlaceholder")}
            autoComplete="off"
          />
          <input
            className="tp-input"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t("admin.notePlaceholder")}
            maxLength={500}
          />
          <button type="submit" className="tp-btn tp-btn-primary tp-btn-block" disabled={!canAdd}>
            {busy ? t("common.saving") : t("admin.add")}
          </button>
        </form>

        {err && <div className="tp-error" role="alert" style={{ marginBottom: 16 }}>{err}</div>}

        {entries === null ? (
          <div className="tp-loading">{t("common.loading")}</div>
        ) : entries.length === 0 ? (
          <div className="tp-body" style={{ color: "var(--tp-ink-soft)" }}>{t("admin.empty")}</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {entries.map((entry) => (
              <div
                key={entry.email}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "10px 12px",
                  background: "var(--tp-surface)",
                  border: "2px solid var(--tp-ink)",
                  borderRadius: 14,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="tp-body" style={{ fontSize: 15, overflowWrap: "anywhere" }}>{entry.email}</div>
                  <div className="tp-body" style={{ fontSize: 12, color: "var(--tp-ink-soft)" }}>
                    {entry.note ? `${entry.note} · ` : ""}
                    {new Date(entry.createdAt).toLocaleDateString()}
                  </div>
                </div>
                <button type="button" className="tp-btn" onClick={() => void remove(entry)}>
                  {t("admin.remove")}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
